/**
 * Purchasable things: equipment, vehicles, housing, food.
 *
 * Equipment is data, not code. A `ServiceLine` (see ADR-0003) declares which
 * equipment slots it needs, so adding cleaning or detailing post-V1 is a data
 * change rather than a new subsystem.
 */
import { dollars } from '../core/money.js';

export type EquipmentSlot = 'mower' | 'trimmer' | 'blower' | 'hauling';

export interface EquipmentDef {
  id: string;
  name: string;
  slot: EquipmentSlot;
  price: number;
  /** Multiplier on work speed. 1.0 is the baseline push mower. */
  speed: number;
  /** Additive contribution to job quality, 0..1. */
  quality: number;
  /** Resale value as a fraction of price, used for net worth. */
  resale: number;
  /** Reputation required to be offered for sale. */
  requiresReputation?: number;
  description: string;
}

export const EQUIPMENT: EquipmentDef[] = [
  {
    id: 'mower_push',
    name: 'Used Push Mower',
    slot: 'mower',
    price: dollars(60),
    speed: 1.0,
    quality: 0.42,
    resale: 0.5,
    description: 'Starts on the fourth pull. It is enough to begin.',
  },
  {
    id: 'trimmer_basic',
    name: 'String Trimmer',
    slot: 'trimmer',
    price: dollars(85),
    speed: 1.08,
    quality: 0.16,
    resale: 0.55,
    description: 'Clean edges are the difference between paid and paid again.',
  },
  {
    id: 'blower_basic',
    name: 'Leaf Blower',
    slot: 'blower',
    price: dollars(105),
    speed: 1.1,
    quality: 0.13,
    resale: 0.55,
    description: 'Finishing a job in half the time makes people talk.',
  },
  {
    id: 'mower_self_propelled',
    name: 'Self-Propelled Mower',
    slot: 'mower',
    price: dollars(330),
    speed: 1.38,
    quality: 0.52,
    resale: 0.6,
    requiresReputation: 10,
    description: 'It pulls itself. Your afternoons come back.',
  },
  {
    id: 'trimmer_pro',
    name: 'Commercial Trimmer',
    slot: 'trimmer',
    price: dollars(240),
    speed: 1.14,
    quality: 0.22,
    resale: 0.6,
    requiresReputation: 25,
    description: 'Heavier, faster, and it does not quit at noon.',
  },
  {
    id: 'mower_commercial',
    name: 'Commercial Zero-Turn',
    slot: 'mower',
    price: dollars(1_150),
    speed: 1.95,
    quality: 0.6,
    resale: 0.65,
    requiresReputation: 35,
    description: 'A large lawn stops being an afternoon and becomes an hour.',
  },
  {
    id: 'trailer',
    name: 'Utility Trailer',
    slot: 'hauling',
    price: dollars(480),
    speed: 1.06,
    quality: 0.05,
    resale: 0.7,
    requiresReputation: 20,
    description: 'Carry a full kit to every job instead of half of one.',
  },
];

export const EQUIPMENT_BY_ID = new Map(EQUIPMENT.map((e) => [e.id, e]));

export function getEquipment(id: string): EquipmentDef {
  const found = EQUIPMENT_BY_ID.get(id);
  if (!found) throw new Error(`Unknown equipment: ${id}`);
  return found;
}

/**
 * A complete second kit, required before an employee can work a job.
 * Priced below buying the parts separately: scaling should feel like a
 * decision, not a punishment.
 */
export const EMPLOYEE_KIT = {
  id: 'employee_kit',
  name: 'Second Crew Kit',
  price: dollars(295),
  resale: 0.5,
  description: 'Mower, trimmer and blower for one more pair of hands.',
};

export interface VehicleDef {
  id: string;
  name: string;
  price: number;
  /** Multiplier on travel time. Lower is faster. */
  travelFactor: number;
  /**
   * Whether a full kit fits in it.
   *
   * The difference between something you ride and something you drive, and
   * what decides whether Hillcrest will call. Declared per vehicle rather
   * than inferred from the id, so adding a second bicycle - or a van - does
   * not silently change which districts open.
   */
  carriesKit: boolean;
  resale: number;
  upkeepDaily: number;
  description: string;
}

export const VEHICLES: VehicleDef[] = [
  {
    id: 'bicycle',
    name: 'Second-hand Bicycle',
    price: dollars(95),
    travelFactor: 0.55,
    carriesKit: false,
    resale: 0.5,
    upkeepDaily: 0,
    description: 'Across the neighbourhood in a few minutes. Tools go on your back.',
  },
  {
    id: 'hatchback_old',
    name: 'Tired Hatchback',
    price: dollars(900),
    travelFactor: 0.38,
    carriesKit: true,
    resale: 0.6,
    upkeepDaily: dollars(1.4),
    description: 'Two previous owners, one working window. It starts, and it is yours.',
  },
  {
    id: 'pickup_old',
    name: 'Old Pickup Truck',
    price: dollars(2_400),
    travelFactor: 0.3,
    carriesKit: true,
    resale: 0.7,
    upkeepDaily: dollars(3),
    description: 'The whole kit, every job, no compromises. This is a real business now.',
  },
];

export const VEHICLES_BY_ID = new Map(VEHICLES.map((v) => [v.id, v]));

export interface HousingDef {
  id: string;
  name: string;
  /** One-off cost to move in. */
  deposit: number;
  weeklyRent: number;
  /** Multiplier on energy recovered per hour of sleep. */
  restQuality: number;
  description: string;
}

export const HOUSING: HousingDef[] = [
  {
    id: 'room_shared',
    name: 'Shared Room, 14B Maple St',
    deposit: 0,
    weeklyRent: dollars(120),
    restQuality: 1.0,
    description: 'A bed, a desk, and a housemate who plays bass at midnight.',
  },
  {
    id: 'studio',
    name: 'Studio Apartment, Cedar Way',
    deposit: dollars(420),
    weeklyRent: dollars(205),
    restQuality: 1.25,
    description: 'Your own door, your own kitchen. You sleep properly here.',
  },
];

export const HOUSING_BY_ID = new Map(HOUSING.map((h) => [h.id, h]));

export function getHousing(id: string): HousingDef {
  const found = HOUSING_BY_ID.get(id);
  if (!found) throw new Error(`Unknown housing: ${id}`);
  return found;
}

export interface FoodDef {
  id: string;
  name: string;
  price: number;
  hunger: number;
  energy: number;
}

export const FOOD: FoodDef[] = [
  { id: 'instant_noodles', name: 'Instant Noodles', price: dollars(2.5), hunger: 22, energy: 2 },
  { id: 'sandwich', name: 'Deli Sandwich', price: dollars(7), hunger: 45, energy: 6 },
  { id: 'hot_meal', name: 'Hot Meal', price: dollars(13), hunger: 70, energy: 12 },
  { id: 'coffee', name: 'Coffee', price: dollars(3.5), hunger: 4, energy: 15 },
];

export const FOOD_BY_ID = new Map(FOOD.map((f) => [f.id, f]));

/**
 * A service line is a category of work the business can perform.
 * V1 ships one (yard care); the shape is what makes more of them cheap later.
 */
export interface ServiceLineDef {
  id: string;
  name: string;
  /** Equipment slots that contribute to speed and quality for this line. */
  slots: EquipmentSlot[];
  /** The slot that must be filled at all, or no work is possible. */
  requiredSlot: EquipmentSlot;
}

export const SERVICE_LINES: ServiceLineDef[] = [
  {
    id: 'yard_care',
    name: 'Yard & Property Care',
    slots: ['mower', 'trimmer', 'blower', 'hauling'],
    requiredSlot: 'mower',
  },
];

/** Names used for procedurally generated customers and employees. */
export const FIRST_NAMES = [
  'Dana', 'Marcus', 'Priya', 'Tom', 'Elena', 'Jamal', 'Rosa', 'Ken',
  'Aisha', 'Victor', 'Nina', 'Owen', 'Grace', 'Luis', 'Mei', 'Frank',
  'Sofia', 'Theo', 'Ada', 'Hassan', 'Clara', 'Ivan', 'Noor', 'Pete',
];

export const LAST_NAMES = [
  'Okafor', 'Reyes', 'Novak', 'Bennett', 'Haddad', 'Lindqvist', 'Moreau',
  'Tanaka', 'Silva', 'Kowalski', 'Osei', 'Brennan', 'Vargas', 'Petrov',
  'Nakamura', 'Duarte', 'Wexler', 'Fontaine',
];
